import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaShieldAlt } from 'react-icons/fa';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold mb-3">{title}</h2>
    <div className="text-sm leading-relaxed space-y-3 text-gray-600">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const isAuthenticated = !!sessionStorage.getItem('token');

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center space-x-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="privacy-back-btn"
          >
            <FaArrowLeft className="text-gray-700" />
          </button>
          <div className="flex items-center space-x-2">
            <FaShieldAlt className="text-[#4A90E2]" />
            <h1 className="text-xl font-bold">Privacy Policy</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="privacy-policy-page">
        <p className="text-xs text-gray-400 mb-6">Last updated: February 1, 2026</p>

        <Section title="1. Introduction">
          <p>
            GDM (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates the TalkinPoint platform and associated services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our social media platform, including text posts, image sharing, video content, live streaming (PointLive), short-form clips (PointClips), cross-posting features, debate arenas (PointArena), and all related services.
          </p>
          <p>
            By accessing or using TalkinPoint, you agree to the collection and use of information in accordance with this policy. If you do not agree, please discontinue use of the platform.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>Account Information:</strong> When you register, we collect your full name, email address, username, and password. You may optionally provide a profile photo, bio, location, and website URL.</p>
          <p><strong>Content You Create:</strong> Posts (text, images, videos, polls, audio), comments, likes, reposts, bookmarks, PointClips, PointArena debate entries, and scheduled or drafted content.</p>
          <p><strong>Communications:</strong> Direct messages, group chat messages, and any content shared through the messaging feature.</p>
          <p><strong>Cross-Posting Data:</strong> When you connect external social media accounts (X/Twitter, Facebook, Instagram, LinkedIn, TikTok, YouTube), we store authentication tokens necessary to publish content on your behalf. We do not store your passwords for these external services.</p>
          <p><strong>Live Streaming:</strong> PointLive streams may be recorded and stored temporarily. Viewer counts, reactions, and chat messages during streams are collected.</p>
          <p><strong>Usage Data:</strong> We automatically collect device information, IP address, browser type, pages visited, time spent, click patterns, and feature usage analytics.</p>
          <p><strong>Verification Data:</strong> If you apply for verification, we collect the submitted documents, government ID, and any supporting materials you provide.</p>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Provide, maintain, and improve the TalkinPoint platform</li>
            <li>Enable cross-posting of your content to connected social media platforms</li>
            <li>Process account verification requests</li>
            <li>Deliver notifications about likes, comments, follows, and other interactions</li>
            <li>Power the Point of the Day (POTD) gamification system</li>
            <li>Facilitate PointArena debates and scoring</li>
            <li>Operate the Creators Hub analytics and monetization features</li>
            <li>Detect and prevent fraud, abuse, and security threats</li>
            <li>Personalize your feed and content recommendations</li>
            <li>Communicate with you about service updates, security alerts, and support</li>
          </ul>
        </Section>

        <Section title="4. Information Sharing & Disclosure">
          <p>We do not sell your personal information. We may share information in these circumstances:</p>
          <p><strong>Public Content:</strong> Posts, profile information, and PointClips you make public are visible to all users and may be indexed by search engines.</p>
          <p><strong>Connected Platforms:</strong> When you use cross-posting, your content is shared with the platforms you have connected (subject to their own privacy policies).</p>
          <p><strong>Service Providers:</strong> We share data with trusted third-party vendors who assist us in operating the platform (hosting, analytics, payment processing, content delivery).</p>
          <p><strong>Legal Requirements:</strong> We may disclose your information if required by law, regulation, legal process, or governmental request.</p>
          <p><strong>Safety:</strong> We may share information when we believe it is necessary to protect the safety, rights, or property of GDM, our users, or the public.</p>
        </Section>

        <Section title="5. Data Retention">
          <p>We retain your account data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law or for legitimate business purposes (e.g., fraud prevention).</p>
          <p>Suspended accounts retain data in a dormant state until reactivation or until you request permanent deletion.</p>
          <p>Content you have cross-posted to external platforms is governed by those platforms' retention policies and is not removed by deleting your TalkinPoint account.</p>
        </Section>

        <Section title="6. Data Security">
          <p>We implement industry-standard security measures to protect your information, including encrypted data transmission (TLS/SSL), hashed passwords, secure token-based authentication, and access controls. However, no method of electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
        </Section>

        <Section title="7. Your Rights & Choices">
          <p>You have the right to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Access</strong> your personal data through your profile and settings</li>
            <li><strong>Edit or correct</strong> your account information at any time</li>
            <li><strong>Delete</strong> your account and associated data</li>
            <li><strong>Suspend</strong> your account temporarily without data loss</li>
            <li><strong>Control privacy</strong> settings including private account mode, DM restrictions, and activity visibility</li>
            <li><strong>Disconnect</strong> linked social media accounts at any time</li>
            <li><strong>Download</strong> a copy of your data (where available)</li>
          </ul>
        </Section>

        <Section title="8. Cookies & Tracking">
          <p>TalkinPoint uses local storage and session tokens for authentication and preference persistence (e.g., dark mode). We may use analytics tools to understand platform usage patterns. You can manage cookie preferences through your browser settings.</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>TalkinPoint is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13. If we learn that we have collected data from a child under 13, we will take steps to delete that information promptly.</p>
        </Section>

        <Section title="10. International Data Transfers">
          <p>Your information may be transferred to and processed in countries other than your country of residence. By using TalkinPoint, you consent to such transfers. We ensure appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.</p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on TalkinPoint and updating the "Last updated" date. Your continued use of the platform after changes constitutes acceptance of the revised policy.</p>
        </Section>

        <Section title="12. Contact Us">
          <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us:</p>
          <p><strong>GDM</strong><br />Email: privacy@gdmhq.com<br />Website: <a href="https://www.gdmhq.com" target="_blank" rel="noopener noreferrer" className="text-[#4A90E2] hover:underline">www.gdmhq.com</a></p>
        </Section>

        {/* Footer nav */}
        <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
          <span>GDM - TalkinPoint</span>
          <div className="flex space-x-4">
            <Link to="/terms" className="hover:text-[#4A90E2] transition-colors" data-testid="privacy-to-terms-link">Terms of Use</Link>
            {isAuthenticated && <Link to="/settings" className="hover:text-[#4A90E2] transition-colors">Settings</Link>}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
